import styled from "styled-components";
import { Link } from "react-router-dom";
import { tokens } from "../../../public/tokens";
import ButtonGroup from "../../library/ButtonGroup";

const { colors, typography, globals, effects } = tokens;
const { neutral, states, text: textColors, ui, brand } = colors;

const mobileQuery = `@media (max-width: 768px)`;

/**
 * Pill colors per condition - each badge also prints its label, so color is never the only cue.
 */
export const conditionColors = {
    ok: { background: states.success["100"], color: states.success["800"] },
    low: { background: states.warning["100"], color: states.warning["800"] },
    damaged: { background: states.alert["100"], color: states.alert["700"] },
    expired: { background: states.alert["200"], color: states.alert["900"] },
    expiring_soon: { background: states.warning["200"], color: states.warning["900"] },
    not_yet_checked: { background: neutral.grey["200"], color: neutral.grey["700"] },
};

/**
 * Chart fills validated with the dataviz palette script (green/red keep CVD separation; the
 * neutral "not yet checked" share is the bar track, not a coloured series).
 */
export const chartColors = {
    compliant: states.success.default,
    nonCompliant: states.alert.default,
    track: neutral.grey["200"],
    single: brand.blue["500"],
};

const cardBase = {
    backgroundColor: neutral.white,
    border: `1px solid ${ui.border}`,
    borderRadius: globals["border-radius"],
    boxShadow: effects.effects.shadow.default,
};

const smallText = {
    fontFamily: globals["font-family"],
    fontSize: typography.fontSize["md-12px"],
    color: textColors.secondary,
};

export const ConditionBadge = styled.span(({ $condition }) => ({
    ...(conditionColors[$condition] || conditionColors.not_yet_checked),
    display: "inline-flex",
    alignItems: "center",
    padding: "2px 10px",
    borderRadius: "999px",
    fontFamily: globals["font-family"],
    fontSize: typography.fontSize["md-12px"],
    fontWeight: typography.fontWeights.semibold,
    whiteSpace: "nowrap",
}));

export const ConditionBadgeRow = styled.span({
    display: "inline-flex",
    flexWrap: "wrap",
    gap: "4px",
});

export const TagStepStack = styled.div({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: globals["spacing-4"],
    marginBottom: globals["spacing-4"],
});

export const TagResultMessage = styled.div(({ $condition }) => ({
    ...conditionColors[$condition],
    display: "flex",
    alignItems: "center",
    gap: globals["spacing-2"],
    padding: `${globals["spacing-2"]} ${globals["spacing-3"]}`,
    borderRadius: globals["border-radius"],
    fontFamily: globals["font-family"],
    fontSize: typography.fontSize["lg-14px"],
    fontWeight: typography.fontWeights.semibold,
}));

export const DaysLeftBadge =styled.span(({ $isUrgent }) => ({
    ...(($isUrgent) ? conditionColors.expired : conditionColors.low),
    display: "inline-flex",
    padding: "2px 10px",
    borderRadius: "999px",
    fontFamily: globals["font-family"],
    fontSize: typography.fontSize["md-12px"],
    fontWeight: typography.fontWeights.semibold,
    whiteSpace: "nowrap",
}));

export const SectionCard = styled.div({
    ...cardBase,
    padding: globals["spacing-6"],
    marginBottom: globals["spacing-5"],

    [mobileQuery]: {
        padding: globals["spacing-4"],
    },
});

export const SectionHeader = styled.div({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: globals["spacing-3"],
    marginBottom: globals["spacing-4"],
    flexWrap: "wrap",
});

export const SectionTitle = styled.h5({
    margin: 0,
    fontFamily: globals["font-family"],
    fontSize: typography.fontSize["xl-18px"],
    fontWeight: typography.fontWeights.semibold,
    color: textColors.primary,
});

export const MutedText = styled.span({
    ...smallText,
});

export const EmptyState = styled.p({
    ...smallText,
    fontSize: typography.fontSize["lg-14px"],
    margin: 0,
    padding: `${globals["spacing-4"]} 0`,
});

export const StatTileGrid = styled.div({
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: globals["spacing-4"],
    marginBottom: globals["spacing-5"],

    [mobileQuery]: {
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    },
});

export const StatTile = styled.div({
    ...cardBase,
    padding: globals["spacing-5"],
    display: "flex",
    flexDirection: "column",
    gap: globals["spacing-1"],
});

export const StatValue = styled.span(({ $needsAttention }) => ({
    fontFamily: globals["font-family"],
    fontSize: typography.fontSize["4xl-32px"],
    fontWeight: typography.fontWeights.bold,
    lineHeight: "110%",
    color: $needsAttention ? states.alert["600"] : textColors.primary,
}));

export const StatLabel = styled.span({
    fontFamily: globals["font-family"],
    fontSize: typography.fontSize["lg-14px"],
    fontWeight: typography.fontWeights.semibold,
    color: textColors.primary,
});

export const CampusGroupTitle = styled.h6({
    ...smallText,
    margin: `${globals["spacing-5"]} 0 ${globals["spacing-2"]}`,
    fontWeight: typography.fontWeights.bold,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
});

export const BoxCardLink = styled(Link)({
    ...cardBase,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: globals["spacing-3"],
    padding: `${globals["spacing-3"]} ${globals["spacing-4"]}`,
    marginBottom: globals["spacing-2"],
    borderLeft: `4px solid ${colors.ui.secondary}`,
    color: textColors.primary,

    "&:hover": {
        boxShadow: effects.effects.shadow.hover,
        textDecoration: "none",
        color: textColors.primary,
    },
});

export const BoxCardTitle = styled.span({
    display: "block",
    fontFamily: globals["font-family"],
    fontSize: typography.fontSize["lg-14px"],
    fontWeight: typography.fontWeights.semibold,
});

export const BoxCardMeta = styled.span({
    ...smallText,
    display: "block",
});

export const TwoColumnGrid = styled.div({
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: globals["spacing-5"],

    "& > *": {
        minWidth: 0,
    },

    [mobileQuery]: {
        gridTemplateColumns: "minmax(0, 1fr)",
    },
});

export const FormGrid = styled.div({
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: globals["spacing-4"],
    marginBottom: globals["spacing-5"],

    "& > *": {
        minWidth: 0,
    },

    "& > .ohs-field--full": {
        gridColumn: "1 / -1",
    },

    [mobileQuery]: {
        gridTemplateColumns: "minmax(0, 1fr)",
    },
});

export const CategoryHeading = styled.h6({
    ...smallText,
    margin: `${globals["spacing-5"]} 0 ${globals["spacing-2"]}`,
    fontWeight: typography.fontWeights.bold,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
});

export const CheckItemRow = styled.div({
    display: "grid",
    gridTemplateColumns: "minmax(180px, 2fr) minmax(130px, 1fr) minmax(110px, 1fr) minmax(150px, 1fr) minmax(160px, 2fr)",
    gap: globals["spacing-3"],
    alignItems: "start",
    padding: `${globals["spacing-3"]} 0`,
    borderBottom: `1px solid ${ui.border}`,

    "& > *": {
        minWidth: 0,
    },

    [mobileQuery]: {
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",

        "& > :first-child": {
            gridColumn: "1 / -1",
        },
    },
});

export const UsageItemRow = styled.div({
    display: "grid",
    gridTemplateColumns: "minmax(200px, 2fr) minmax(130px, 1fr)",
    gap: globals["spacing-3"],
    alignItems: "start",
    padding: `${globals["spacing-3"]} 0`,
    borderBottom: `1px solid ${ui.border}`,

    "& > *": {
        minWidth: 0,
    },

    [mobileQuery]: {
        gridTemplateColumns: "minmax(0, 1fr)",
        gap: globals["spacing-2"],
    },
});

export const QuestionRow = styled.div({
    display: "grid",
    gridTemplateColumns: "minmax(0, 3fr) max-content minmax(200px, 2fr)",
    gap: globals["spacing-4"],
    alignItems: "start",
    padding: `${globals["spacing-3"]} 0`,
    borderBottom: `1px solid ${ui.border}`,

    "& > *": {
        minWidth: 0,
    },

    [mobileQuery]: {
        gridTemplateColumns: "minmax(0, 1fr)",
        gap: globals["spacing-2"],
    },
});

export const QuestionText = styled.div({
    display: "flex",
    gap: globals["spacing-2"],
    paddingTop: globals["spacing-2"],
    fontFamily: globals["font-family"],
    fontSize: typography.fontSize["lg-14px"],
    color: textColors.primary,
});

export const QuestionNumber = styled.span({
    flexShrink: 0,
    minWidth: "24px",
    fontWeight: typography.fontWeights.semibold,
});

// position: relative keeps the library Radio/Checkbox hidden inputs (position: absolute) inside their
// group - unanchored, they're placed against the page and stretch it past the scrolling content area.
export const CheckboxList = styled.div({
    position: "relative",
    display: "flex",
    flexDirection: "column",
    gap: globals["spacing-3"],
    marginTop: globals["spacing-2"],
});

export const AnswerOptions = styled.div({
    position: "relative",
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: globals["spacing-4"],
    minHeight: "36px",
});

// The sidebar turns into a fixed bottom nav on mobile, which covers the buttons at the end of the box forms.
export const DesktopOnlyNav = styled.div(({ $isDesktopOnly = true }) => ({
    [mobileQuery]: {
        display: $isDesktopOnly ? "none" : "block",
    },
}));

export const WrappingButtonGroup = styled(ButtonGroup)({
    flexWrap: "wrap",
});

// handleTableScroll makes each row's first/last cell sticky (z-index 11), so a later row's Action cell
// paints over an open "More" menu, and on mobile the two sticky columns leave almost no room to scroll.
export const OhsTabContent = styled.div({
    "& .when-scroll-sticky .rdt_TableBody .rdt_TableRow .rdt_TableCell:last-child:has(.dropdown-menu.show)": {
        zIndex: 12,
    },

    [mobileQuery]: {
        "& .when-scroll-sticky .rdt_TableBody .rdt_TableRow .rdt_TableCell:first-child, & .when-scroll-sticky .rdt_TableHead .rdt_TableHeadRow .rdt_TableCol:first-child": {
            position: "static",
        },
    },
});

export const CheckItemName = styled.div({
    fontFamily: globals["font-family"],
    fontSize: typography.fontSize["lg-14px"],
    fontWeight: typography.fontWeights.semibold,
    color: textColors.primary,
    paddingTop: globals["spacing-2"],
});

export const ConfirmationPanel = styled.div({
    ...cardBase,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: globals["spacing-3"],
    padding: `${globals["spacing-10"]} ${globals["spacing-6"]}`,
    textAlign: "center",
    fontFamily: globals["font-family"],

    "& i": {
        fontSize: typography.fontSize["4xl-32px"],
        color: states.success.default,
    },
});

export const ChartLegend = styled.div({
    ...smallText,
    display: "flex",
    flexWrap: "wrap",
    gap: globals["spacing-4"],
    marginBottom: globals["spacing-4"],
});

export const LegendItem = styled.span({
    display: "inline-flex",
    alignItems: "center",
    gap: globals["spacing-1-5"],
});

export const LegendSwatch = styled.span(({ $color }) => ({
    display: "inline-block",
    width: "10px",
    height: "10px",
    borderRadius: "2px",
    backgroundColor: $color,
}));

export const RankedBarRow = styled.div({
    display: "grid",
    gridTemplateColumns: "minmax(140px, 220px) minmax(0, 1fr) minmax(140px, max-content)",
    alignItems: "center",
    gap: globals["spacing-3"],
    padding: `${globals["spacing-1-5"]} 0`,

    [mobileQuery]: {
        gridTemplateColumns: "minmax(0, 1fr) max-content",

        "& > :nth-child(2)": {
            gridColumn: "1 / -1",
            gridRow: 2,
        },
    },
});

export const RankedBarLabel = styled.span({
    fontFamily: globals["font-family"],
    fontSize: typography.fontSize["lg-14px"],
    color: textColors.primary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
});

export const RankedBarTrack = styled.div({
    display: "flex",
    gap: "2px",
    height: "12px",
    backgroundColor: neutral.white,
});

// flex-grow (not width %) so the 2px surface gaps never push the segments past 100%.
export const RankedBarSegment = styled.div(({ $color, $share }) => ({
    flex: `${$share} 0 0`,
    backgroundColor: $color,
    borderRadius: "4px",
}));

export const RankedBarValue = styled.span({
    ...smallText,
    textAlign: "right",
    whiteSpace: "nowrap",
});
